import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BaseClinicalSession,
  CreateClinicalSession,
  UpdateClinicalSession,
  ClinicalSessionStatus,
} from '../dtos/ClinicalSessions/index';

class ClinicalSessionApi extends BaseApi {
  private readonly basePath = '/clinical-sessions';

  /**
   * Get clinical session by ID
   * GET /api/clinical-sessions/{id}
   */
  async getById(id: string): Promise<Response<BaseClinicalSession | null>> {
    return this.get<BaseClinicalSession | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all clinical sessions
   * GET /api/clinical-sessions
   */
  async getList(): Promise<Response<BaseClinicalSession[]>> {
    return this.get<BaseClinicalSession[]>(this.basePath);
  }

  /**
   * Get clinical sessions by user ID
   * GET /api/clinical-sessions/user/{userId}
   */
  async getByUserId(userId: string): Promise<Response<BaseClinicalSession[]>> {
    return this.get<BaseClinicalSession[]>(`${this.basePath}/user/${userId}`);
  }

  /**
   * Get clinical sessions by person ID
   * GET /api/clinical-sessions/person/{personId}
   */
  async getByPersonId(personId: string): Promise<Response<BaseClinicalSession[]>> {
    return this.get<BaseClinicalSession[]>(`${this.basePath}/person/${personId}`);
  }

  /**
   * Get paginated list of clinical sessions
   * GET /api/clinical-sessions/paged?pageNumber=1&pageSize=10&status=0
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    status?: ClinicalSessionStatus | null
  ): Promise<Response<PagedResponse<BaseClinicalSession>>> {
    let url = `${this.basePath}/paged?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (status !== null && status !== undefined) {
      url += `&status=${status}`;
    }
    return this.get<PagedResponse<BaseClinicalSession>>(url);
  }

  /**
   * Create a new clinical session
   * POST /api/clinical-sessions
   */
  async create(data: CreateClinicalSession): Promise<Response<BaseClinicalSession>> {
    return this.post<BaseClinicalSession, CreateClinicalSession>(this.basePath, data);
  }

  /**
   * Update an existing clinical session
   * PUT /api/clinical-sessions/{id}
   */
  async update(id: string, data: Omit<UpdateClinicalSession, 'id'>): Promise<Response<BaseClinicalSession>> {
    return this.put<BaseClinicalSession, UpdateClinicalSession>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete a clinical session
   * DELETE /api/clinical-sessions/{id}
   */
  async deleteClinicalSession(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const clinicalSessionApi = new ClinicalSessionApi();

export default ClinicalSessionApi;
